import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Download, Play, Save, ShieldCheck } from 'lucide-react';
import { reportsAPI } from '../../api';
import { useAppStore } from '../../stores/useAppStore';
import { downloadResponse } from '../../utils/download';
import { Button } from '../ui';
import DateRangeControl from './DateRangeControl';
import ResultsGrid from './ResultsGrid';
import SaveReportModal from './SaveReportModal';
import { extractApiError, summarise } from './lib/apiErrors';
import { useReportCatalog } from './useReportCatalog';
import { useReportRun, userTimezone } from './useReportRun';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';

const PARAMS = [
  { name: ':start_utc', hint: 'range start as UTC datetime (inclusive)' },
  { name: ':end_utc', hint: 'range end as UTC datetime (exclusive)' },
  { name: ':start_local', hint: 'range start as a local date' },
  { name: ':end_local', hint: 'range end as a local date' },
  { name: ':user_id', hint: 'id of the user running the report' },
];

const DEFAULT_SQL = `SELECT DATE(SaleDate) AS day, COUNT(*) AS bills, SUM(CashReceived + BankReceived - CashReturned) AS charged
FROM sales
WHERE SaleDate >= :start_utc AND SaleDate < :end_utc
GROUP BY DATE(SaleDate)
ORDER BY day`;

/**
 * Admin-only read-only SQL editor. Saving creates a Kind='sql' report that
 * managers can run with their own date range.
 */
const SqlWorkbench = ({ report = null }) => {
  const navigate = useNavigate();
  const { addNotification } = useAppStore();
  const { presets, sqlModeEnabled } = useReportCatalog();
  const textareaRef = useRef(null);

  const [sql, setSql] = useState(report?.SqlText || DEFAULT_SQL);
  const [savedSql, setSavedSql] = useState(report?.SqlText || null);
  const [usesDateRange, setUsesDateRange] = useState(report?.Parameters?.uses_date_range ?? true);
  const [savedMeta, setSavedMeta] = useState(report ? { id: report.ID, name: report.Name, description: report.Description || '' } : null);
  const [dateRange, setDateRange] = useState({ field: null, preset: report?.Parameters?.default_preset || 'this_month', start: null, end: null });
  const [saveOpen, setSaveOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [exporting, setExporting] = useState(null);

  const isDirty = sql !== savedSql || (report ? usesDateRange !== !!report.Parameters?.uses_date_range : false);
  const confirmLeave = useUnsavedChangesGuard(isDirty);

  const params = useMemo(
    () => ({ date_range: { preset: dateRange.preset, start: dateRange.start || null, end: dateRange.end || null } }),
    [dateRange]
  );
  const execute = useCallback((base) => reportsAPI.sqlPreview(sql, { ...base, ...params }), [sql, params]);
  const key = JSON.stringify({ sql, params });
  const run = useReportRun({ execute, key });

  useEffect(() => {
    const handler = (e) => {
      if (saveOpen) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        run.run();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        setSaveError(null);
        setSaveOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [run, saveOpen]);

  const insertAtCursor = (text) => {
    const el = textareaRef.current;
    if (!el) {
      setSql((s) => s + text);
      return;
    }
    const start = el.selectionStart ?? sql.length;
    const end = el.selectionEnd ?? sql.length;
    const next = sql.slice(0, start) + text + sql.slice(end);
    setSql(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + text.length, start + text.length);
    });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      insertAtCursor('  ');
    }
  };

  const submitSave = async (values) => {
    setSaving(true);
    setSaveError(null);
    try {
      const Parameters = { uses_date_range: usesDateRange, default_preset: dateRange.preset === 'custom' ? 'this_month' : dateRange.preset };
      if (savedMeta?.id) {
        const res = await reportsAPI.update(savedMeta.id, { Name: values.name.trim(), Description: values.description?.trim() || null, SqlText: sql, Parameters });
        setSavedMeta({ id: res.data.ID, name: res.data.Name, description: res.data.Description || '' });
        setSavedSql(sql);
        addNotification({ type: 'success', message: 'SQL report saved' });
        setSaveOpen(false);
      } else {
        const res = await reportsAPI.create({ Name: values.name.trim(), Description: values.description?.trim() || null, Kind: 'sql', SqlText: sql, Parameters, IsActive: true });
        setSavedMeta({ id: res.data.ID, name: res.data.Name, description: res.data.Description || '' });
        setSavedSql(sql);
        setSaveOpen(false);
        addNotification({ type: 'success', message: `SQL report "${res.data.Name}" created` });
        navigate(`/reports/${res.data.ID}/edit`, { replace: true });
      }
    } catch (err) {
      setSaveError(summarise(await extractApiError(err)));
    } finally {
      setSaving(false);
    }
  };

  const doExport = async (format) => {
    setExporting(format);
    try {
      const res = await reportsAPI.sqlExport(sql, { user_timezone: userTimezone(), ...params }, format);
      downloadResponse(res, `sql-report.${format}`);
    } catch (err) {
      addNotification({ type: 'error', message: summarise(await extractApiError(err)) });
    } finally {
      setExporting(null);
    }
  };

  if (!sqlModeEnabled) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">SQL mode is disabled</h1>
        <p className="text-gray-600 mb-6">
          The read-only SQL workbench is turned off. An administrator can enable it by setting
          <code className="mx-1 rounded bg-gray-100 px-1 py-0.5 text-sm">REPORTS_SQL_MODE_ENABLED=true</code>
          with a restricted <code className="mx-1 rounded bg-gray-100 px-1 py-0.5 text-sm">REPORTS_DATABASE_URL</code>.
        </p>
        <Button onClick={() => navigate('/reports')}>Back to reports</Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => {
              if (confirmLeave()) navigate('/reports');
            }}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-800"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Reports
          </button>
          <h1 className="text-xl font-bold text-gray-900 truncate flex items-center gap-2">
            {savedMeta?.name || 'SQL Workbench'}
            {isDirty && <span className="w-2 h-2 rounded-full bg-amber-500" title="Unsaved changes" />}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" disabled={!!exporting} onClick={() => doExport('csv')}>
            <Download className="w-4 h-4 mr-1" /> {exporting === 'csv' ? '…' : 'CSV'}
          </Button>
          <Button variant="secondary" disabled={!!exporting} onClick={() => doExport('xlsx')}>
            <Download className="w-4 h-4 mr-1" /> {exporting === 'xlsx' ? '…' : 'Excel'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setSaveError(null);
              setSaveOpen(true);
            }}
            title="Ctrl+S"
          >
            <Save className="w-4 h-4 mr-1" /> {savedMeta?.id ? 'Save' : 'Save as report'}
          </Button>
          <Button onClick={() => run.run()} disabled={run.isRunning} title="Ctrl+Enter">
            <Play className="w-4 h-4 mr-1" /> {run.isRunning ? 'Running…' : 'Run'}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <textarea
            ref={textareaRef}
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            onKeyDown={onKeyDown}
            spellCheck={false}
            className="w-full min-h-[280px] font-mono text-sm rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            placeholder="SELECT … FROM sales WHERE SaleDate >= :start_utc AND SaleDate < :end_utc"
          />
        </div>
        <div className="space-y-3">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Parameters</h3>
            <DateRangeControl value={dateRange} onChange={(patch) => setDateRange((d) => ({ ...d, ...patch, ...(patch.preset && patch.preset !== 'custom' ? { start: null, end: null } : {}) }))} presets={presets} allowAll={false} applied={run.result?.date_range_applied} />
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" checked={usesDateRange} onChange={(e) => setUsesDateRange(e.target.checked)} />
              Let viewers pick the date range when running
            </label>
            <div className="flex flex-wrap gap-1.5">
              {PARAMS.map((p) => (
                <button key={p.name} type="button" onClick={() => insertAtCursor(p.name)} title={`${p.hint} — click to insert`} className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700 hover:bg-gray-200">
                  {p.name}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 flex gap-2">
            <ShieldCheck className="w-4 h-4 shrink-0 text-gray-400" />
            <p>Read-only SELECT statements only. The server rejects anything else, strips comments, caps rows and runs inside a read-only transaction.</p>
          </div>
        </div>
      </div>

      {run.result?.warnings?.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>{run.result.warnings.join(' ')}</div>
        </div>
      )}
      {run.isStale && run.result && <p className="text-xs rounded bg-amber-100 text-amber-800 px-2 py-1 inline-block">Query changed — run to refresh.</p>}

      <ResultsGrid
        result={run.result}
        status={run.status}
        error={run.error}
        groupBy={[]}
        page={run.page}
        pageSize={run.pageSize}
        onPageChange={(p) => run.setPage(p)}
        onPageSizeChange={(s) => run.setPageSize(s)}
        stale={run.isStale}
        emptyHint="Run (Ctrl+Enter) to execute the query"
      />

      <SaveReportModal
        isOpen={saveOpen}
        onClose={() => setSaveOpen(false)}
        title={savedMeta?.id ? 'Save SQL report' : 'Save as SQL report'}
        initialValues={{ name: savedMeta?.name || '', description: savedMeta?.description || '' }}
        onSubmit={submitSave}
        serverError={saveError}
        submitting={saving}
      />
    </div>
  );
};

export default SqlWorkbench;
